import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const VisitorStatsTab = () => {
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [locationStats, setLocationStats] = useState([]);
  const [monthStats, setMonthStats] = useState([]);
  const [error, setError] = useState(null);

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [countRes, locationRes, monthRes] = await Promise.all([
        supabase.rpc('get_mi_unique_visitor_count'),
        supabase.rpc('get_mi_visitor_stats_by_location'),
        supabase.rpc('get_mi_visitor_stats_by_month'),
      ]);

      if (countRes?.error) throw countRes.error;
      if (locationRes?.error) throw locationRes.error;
      if (monthRes?.error) throw monthRes.error;

      setTotalCount(countRes?.data || 0);
      setLocationStats(locationRes?.data || []);
      setMonthStats(monthRes?.data || []);
    } catch (e) {
      setError(e?.message || 'Failed to load visitor stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Icon name="Loader2" size={24} className="animate-spin" style={{ color: '#1e4a72' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg p-4 text-sm" style={{ backgroundColor: '#fee2e2', color: '#991b1b' }}>
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Total count card */}
      <div className="flex items-center gap-4 rounded-xl p-5 border" style={{ borderColor: '#e5e7eb', backgroundColor: '#dbeafe' }}>
        <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#1e4a72' }}>
          <Icon name="Users" size={22} color="#ffffff" />
        </div>
        <div>
          <p className="text-2xl font-extrabold" style={{ color: '#1e4a72' }}>{totalCount?.toLocaleString('en-IN')}</p>
          <p className="text-xs font-medium" style={{ color: '#6b7280' }}>Total Unique Visitors (Market Intelligence)</p>
        </div>
        <button
          onClick={loadStats}
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition hover:bg-white"
          style={{ borderColor: '#1e4a72', color: '#1e4a72' }}
        >
          <Icon name="RefreshCw" size={13} />
          Refresh
        </button>
      </div>

      {/* Location breakdown */}
      <section>
        <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: '#1e4a72' }}>
          <Icon name="MapPin" size={16} />
          By Location
        </h3>
        <div className="overflow-x-auto rounded-lg border" style={{ borderColor: '#e5e7eb' }}>
          <table className="w-full text-xs">
            <thead>
              <tr style={{ backgroundColor: '#f9fafb' }}>
                <th className="text-left px-4 py-2.5 font-semibold" style={{ color: '#6b7280' }}>City</th>
                <th className="text-left px-4 py-2.5 font-semibold" style={{ color: '#6b7280' }}>State/Region</th>
                <th className="text-left px-4 py-2.5 font-semibold" style={{ color: '#6b7280' }}>Country</th>
                <th className="text-right px-4 py-2.5 font-semibold" style={{ color: '#6b7280' }}>Visitors</th>
              </tr>
            </thead>
            <tbody>
              {locationStats?.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-6 text-center" style={{ color: '#9ca3af' }}>No data yet</td></tr>
              )}
              {locationStats?.map((row, idx) => (
                <tr key={idx} className="border-t" style={{ borderColor: '#f3f4f6' }}>
                  <td className="px-4 py-2.5">{row?.city || '—'}</td>
                  <td className="px-4 py-2.5">{row?.region || '—'}</td>
                  <td className="px-4 py-2.5">{row?.country || '—'}</td>
                  <td className="px-4 py-2.5 text-right font-semibold" style={{ color: '#1e4a72' }}>{row?.visitor_count?.toLocaleString('en-IN')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Month-wise breakdown */}
      <section>
        <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: '#1e4a72' }}>
          <Icon name="Calendar" size={16} />
          By Month
        </h3>
        <div className="overflow-x-auto rounded-lg border" style={{ borderColor: '#e5e7eb' }}>
          <table className="w-full text-xs">
            <thead>
              <tr style={{ backgroundColor: '#f9fafb' }}>
                <th className="text-left px-4 py-2.5 font-semibold" style={{ color: '#6b7280' }}>Month</th>
                <th className="text-right px-4 py-2.5 font-semibold" style={{ color: '#6b7280' }}>New Unique Visitors</th>
              </tr>
            </thead>
            <tbody>
              {monthStats?.length === 0 && (
                <tr><td colSpan={2} className="px-4 py-6 text-center" style={{ color: '#9ca3af' }}>No data yet</td></tr>
              )}
              {monthStats?.map((row, idx) => (
                <tr key={idx} className="border-t" style={{ borderColor: '#f3f4f6' }}>
                  <td className="px-4 py-2.5">{row?.month}</td>
                  <td className="px-4 py-2.5 text-right font-semibold" style={{ color: '#1e4a72' }}>{row?.visitor_count?.toLocaleString('en-IN')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default VisitorStatsTab;
